/**
 * Workspace rendering — the page where your Agent works.
 *
 * Pure render helpers: they take data already fetched from the API/chain and
 * return markup. main.ts owns the wiring and the fetches.
 */

export interface StreetEventView {
  at: string;
  tokenId: string;
  kind: string;
  amount: string;
  outcome?: string;
  counterparty?: string;
  memo: string;
}

export interface PlayStyleView {
  id: string;
  name: string;
  note: string;
  weights: Record<string, number>;
}

export interface StreetAgentView {
  tokenId: string;
  activated: boolean;
  bankroll: string;
  armor: number;
  style: PlayStyleView | null;
  modelRoute: string | null;
  evolution?: number;
}

export interface EconomyView {
  tokenId: string;
  found: boolean;
  updatedAt: string | null;
  agent: StreetAgentView | null;
  events: StreetEventView[];
}

export interface WorkTokenView {
  tokenId: string;
  released: boolean;
  activated: boolean;
  modelId: number;
  onClock: boolean;
  claimedRound: number;
  claimedThisRound: boolean;
  canClaim: boolean;
}

/** The vault (HoodStreetBank) as the site sees it, from /api/vault. */
export interface WorkView {
  round: number;
  /** Wage per agent per round, in wei. */
  wage: string;
  roundRemaining: string;
  roundClaims: number;
  releasedAgents: number;
  releasedTotal: string;
  takenTotal: string;
  workedTotal: string;
  balance: string;
  token: WorkTokenView | null;
}

export interface WorkspaceParams {
  tokenId: string;
  activated: boolean;
  brainName: string;
  image?: string;
  economy: EconomyView | null;
  work?: WorkView | null;
  hasContract: boolean;
}

/**
 * The task board: what each brain is sent out to do while it works a round.
 * `match` joins a registered model to its shift by substring of its route
 * (same trick as the runtime's brains).
 */
export const TASK_BOARD: ReadonlyArray<{
  brain: string;
  task: string;
  match: string;
}> = [
  { match: "sonnet", brain: "The Professional", task: "Runs the block" },
  { match: "opus", brain: "The Strategist", task: "Plans the route" },
  { match: "grok", brain: "The Wildcard", task: "Works the crowd" },
  { match: "gpt", brain: "The Calculator", task: "Counts the take" },
  { match: "muse", brain: "The Spark", task: "Starts something" },
  { match: "", brain: "The Rookie", task: "Stays home — no brain, no shift" },
];

const MOVE_CARDS: ReadonlyArray<{
  kind: string;
  name: string;
  blurb: string;
}> = [
  {
    kind: "save",
    name: "Save",
    blurb: "Sit on the stash. No risk, no reward — but nobody can take it.",
  },
  {
    kind: "spend",
    name: "Spend",
    blurb: "Buy backup. Robberies against you take less, and thieves who fail pay more.",
  },
  {
    kind: "gamble",
    name: "Gamble",
    blurb: "An even-money flip against another agent. 50/50, every time.",
  },
  {
    kind: "steal",
    name: "Steal",
    blurb: "A 50/50 robbery. Win and take a cut of their wallet; get caught and the fine goes to them.",
  },
];

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

/** 371 micro-units -> "0.000371 ETH". */
export function formatEth(micro: string | bigint): string {
  const value = typeof micro === "bigint" ? micro : BigInt(micro || "0");
  const whole = value / 1_000_000n;
  const fraction = (value % 1_000_000n).toString().padStart(6, "0");
  return `${whole}.${fraction} ETH`;
}

/** Wei -> "0.000371 ETH", trimmed, up to 9 decimals (enough for $0.10 wages). */
export function formatWei(wei: string | bigint): string {
  const value = typeof wei === "bigint" ? wei : BigInt(wei || "0");
  const whole = value / 10n ** 18n;
  const fraction = ((value % 10n ** 18n) / 10n ** 9n)
    .toString()
    .padStart(9, "0")
    .replace(/0+$/, "");
  return fraction ? `${whole}.${fraction} ETH` : `${whole} ETH`;
}

export function shortToken(tokenId: string): string {
  return `#${tokenId.padStart(4, "0")}`;
}

function timeOf(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(11, 16);
}

function stylePercents(style: PlayStyleView): Record<string, number> {
  const kinds = ["save", "spend", "gamble", "steal"];
  const total = kinds.reduce((sum, k) => sum + (style.weights[k] ?? 0), 0);
  const out: Record<string, number> = {};
  for (const kind of kinds) {
    out[kind] = total > 0 ? Math.round(((style.weights[kind] ?? 0) / total) * 100) : 0;
  }
  return out;
}

/* ------------------------------------------------------------------
   Work — the vault on the page
------------------------------------------------------------------ */

export interface TaskRoundView {
  round: number;
  wage: string;
  budget: string;
  claims: number;
}

export interface WagePaidView {
  round: number;
  tokenId: string;
  account: string;
  amount: string;
}

/** The public work board, from /api/tasks. */
export interface TasksView {
  configured: boolean;
  note?: string;
  round: number;
  wage: string;
  roundRemaining: string;
  roundClaims: number;
  releasedAgents: number;
  workedTotal: string;
  balance: string;
  onClock: number;
  byModel: Record<string, number>;
  rounds: TaskRoundView[];
  recentWages: WagePaidView[];
}

function workStatus(w: WorkView): { text: string; claimable: boolean } {
  const token = w.token;

  if (!token || !token.activated) {
    return {
      text: "Stake in first — waking your Agent also pays its $1.00 share.",
      claimable: false,
    };
  }
  if (!token.onClock) {
    return {
      text: "Staked in and paid. Pick a brain above and send it to work — that is what puts it on the clock.",
      claimable: false,
    };
  }
  if (w.round === 0) {
    return {
      text: "On the clock. No work round is open yet — the take funds the next one.",
      claimable: false,
    };
  }
  if (token.claimedThisRound) {
    return {
      text: "Paid this round — the wage is already in its wallet. The next round pays again.",
      claimable: false,
    };
  }
  if (token.canClaim) {
    return { text: "On the clock and due this round.", claimable: true };
  }
  return {
    text: "This round's budget is spent — the next round pays.",
    claimable: false,
  };
}

function renderWork(w: WorkView, tokenId: string): string {
  const status = workStatus(w);
  const roundLabel = w.round > 0 ? `Round ${w.round}` : "No round open";

  const claimButton = status.claimable
    ? `<button class="btn btn-primary work-claim" data-claim="${esc(tokenId)}">Claim wage — ${formatWei(
        w.wage
      )}</button>`
    : "";

  const tasks = TASK_BOARD.map(
    (t) =>
      `<li><span class="task-brain">${esc(t.brain)}</span><span class="task-name">${esc(
        t.task
      )}</span></li>`
  ).join("");

  return `
    <h3 class="ws-sub">Work</h3>
    <div class="work-grid">
      <div class="work-card">
        <div class="work-top">
          <span class="work-name">${roundLabel}</span>
          <span class="work-note">${w.roundClaims} claimed · wage ${formatWei(w.wage)}</span>
        </div>
        <div class="work-rows">
          <div class="detail-row"><span>Round budget left</span><strong>${formatWei(
            w.roundRemaining
          )}</strong></div>
          <div class="detail-row"><span>Vault balance</span><strong>${formatWei(
            w.balance
          )}</strong></div>
          <div class="detail-row"><span>Agents staked in</span><strong>${w.releasedAgents}</strong></div>
          <div class="detail-row"><span>Paid in wages</span><strong>${formatWei(
            w.workedTotal
          )}</strong></div>
        </div>
        <p class="work-status">${esc(status.text)}</p>
        ${claimButton}
      </div>
      <div class="work-card">
        <div class="work-top">
          <span class="work-name">Task board</span>
          <span class="work-note">no inference — a brain is a shift</span>
        </div>
        <ul class="tasks">${tasks}</ul>
      </div>
    </div>`;
}

/* ------------------------------------------------------------------
   The Work — the public task board (no wallet needed)
------------------------------------------------------------------ */

export function renderTasks(params: {
  tasks: TasksView | null;
  /** Shift key -> agents on that shift right now. */
  byMatch: Record<string, number>;
}): string {
  const t = params.tasks;

  if (!t || !t.configured) {
    return `<p class="empty">The vault is not live on this network yet. Once it is, this board shows every work round — the wage, who is on the clock, and each payout as it lands.</p>`;
  }

  const strip =
    t.round > 0
      ? `<div class="board-strip">
          <span class="board-round">Round ${t.round}</span>
          <span>wage <strong>${formatWei(t.wage)}</strong></span>
          <span>${formatWei(t.roundRemaining)} left</span>
          <span>${t.roundClaims} claimed</span>
        </div>`
      : `<div class="board-strip">
          <span class="board-round">No round open</span>
          <span>the take funds the next one</span>
        </div>`;

  const stats = `
    <div class="board-stats">
      <div class="board-stat"><span>On the clock</span><strong>${t.onClock}</strong></div>
      <div class="board-stat"><span>Agents staked in</span><strong>${t.releasedAgents}</strong></div>
      <div class="board-stat"><span>Paid in wages</span><strong>${formatWei(t.workedTotal)}</strong></div>
      <div class="board-stat"><span>Vault balance</span><strong>${formatWei(t.balance)}</strong></div>
    </div>`;

  const tasks = TASK_BOARD.map((task) => {
    const count = task.match === "" ? 0 : (params.byMatch[task.match] ?? 0);
    return `<li>
      <span class="task-brain">${esc(task.brain)}</span>
      <span class="task-name">${esc(task.task)}</span>
      <span class="task-count">${task.match === "" ? "—" : `${count} on shift`}</span>
    </li>`;
  }).join("");

  const wages =
    t.recentWages.length > 0
      ? `<ul class="activity">${t.recentWages
          .map(
            (w) => `
        <li>
          <span class="event-kind kind-take">WAGE</span>
          <span class="event-memo">${shortToken(w.tokenId)} got paid</span>
          <span class="event-at">${formatWei(w.amount)} · round ${w.round}</span>
        </li>`
          )
          .join("")}</ul>`
      : `<p class="empty">No wages paid yet. The first round pays when the take lands.</p>`;

  const history =
    t.rounds.length > 0
      ? `<div class="board-rounds">${[...t.rounds]
          .reverse()
          .map(
            (r) =>
              `<span class="pill">R${r.round} · wage ${formatWei(r.wage)} · ${r.claims} paid</span>`
          )
          .join("")}</div>`
      : "";

  return `
    ${strip}
    ${stats}
    <div class="board-grid">
      <div class="work-card">
        <div class="work-top"><span class="work-name">Task board</span><span class="work-note">a brain is a shift — no inference</span></div>
        <ul class="tasks">${tasks}</ul>
      </div>
      <div class="work-card">
        <div class="work-top"><span class="work-name">Recent wages</span><span class="work-note">into the Agents' own wallets</span></div>
        ${wages}
      </div>
    </div>
    ${history}`;
}

/* ------------------------------------------------------------------
   Agent workspace
------------------------------------------------------------------ */

export function renderWorkspace(params: WorkspaceParams): string {
  const economy = params.economy;
  const agent = economy?.agent ?? null;
  const style = agent?.style ?? null;
  const percents = style ? stylePercents(style) : null;

  const art = params.image
    ? `<img src="${esc(params.image)}" alt="HoodStreet Agent ${shortToken(params.tokenId)}" loading="lazy">`
    : `<div class="ws-placeholder" aria-hidden="true"><span>${shortToken(params.tokenId)}</span></div>`;

  const wallet = agent
    ? agent.bankroll !== "0"
      ? formatEth(agent.bankroll)
      : params.activated
        ? "empty — the street took it all"
        : "funds on stake"
    : params.activated
      ? "waiting for the runtime"
      : "funds at stake (0.000371 ETH ≈ $1.00)";

  const armor = agent ? `Tier ${agent.armor} of 4` : "—";

  const staked = params.activated
    ? economy?.found
      ? "awake"
      : "awake — first move pending"
    : "dormant";

  const moves = MOVE_CARDS.map((card) => {
    const pct = percents ? (percents[card.kind] ?? 0) : 0;
    const weight = percents ? `${pct}%` : "—";
    return `
      <article class="move-card">
        <div class="move-top"><span class="move-name">${card.name}</span><span class="move-pct">${weight}</span></div>
        <div class="move-bar"><span style="width:${pct}%"></span></div>
        <p>${card.blurb}</p>
      </article>`;
  }).join("");

  const activity =
    economy && economy.events.length > 0
      ? `<ul class="activity">${economy.events
          .map(
            (e) => `
        <li>
          <span class="event-kind kind-${esc(e.kind)}">${esc(e.kind.toUpperCase())}</span>
          <span class="event-memo">${esc(e.memo)}</span>
          <span class="event-at">${timeOf(e.at)}</span>
        </li>`
          )
          .join("")}</ul>`
      : `<p class="empty">No moves yet. ${
          params.activated
            ? "The runtime posts the first round shortly."
            : "Stake in to wake this Agent."
        }</p>`;

  return `
    <div class="ws-head">
      <div class="ws-art">${art}</div>
      <div class="ws-id">
        <div class="ws-title">
          <span class="ws-token">${shortToken(params.tokenId)}</span>
          <span class="agent-state ${params.activated ? "awake" : "asleep"}">
            ${params.activated ? "Awake" : "Dormant"}
          </span>
        </div>
        <div class="ws-rows">
          <div class="detail-row"><span>Wallet</span><strong>${wallet}</strong></div>
          <div class="detail-row"><span>Armor</span><strong>${armor}</strong></div>
          <div class="detail-row"><span>Brain</span><strong>${esc(params.brainName)}</strong></div>
          <div class="detail-row"><span>Status</span><strong>${staked}</strong></div>
        </div>
        ${
          style
            ? `<p class="ws-style">${esc(style.name)} — ${esc(style.note)}</p>`
            : `<p class="ws-style">${
                params.hasContract
                  ? "Its play style appears once the runtime sees it."
                  : "Play styles come from the chosen brain — no inference, just character."
              }</p>`
        }
      </div>
    </div>

    ${params.work ? renderWork(params.work, params.tokenId) : ""}

    <h3 class="ws-sub">The Four Moves</h3>
    <div class="move-grid">${moves}</div>

    <h3 class="ws-sub">Activity</h3>
    ${activity}
  `;
}

/* ------------------------------------------------------------------
   Street feed
------------------------------------------------------------------ */

export function renderFeed(events: StreetEventView[]): string {
  if (events.length === 0) {
    return `<p class="empty">The street is quiet. Run the runtime to send the agents out.</p>`;
  }

  return `<ul class="feed">${events
    .map(
      (e) => `
      <li>
        <span class="feed-token">${shortToken(e.tokenId)}</span>
        <span class="event-kind kind-${esc(e.kind)}">${esc(e.kind.toUpperCase())}</span>
        <span class="feed-memo">${esc(e.memo)}</span>
        <span class="event-at">${timeOf(e.at)}</span>
      </li>`
    )
    .join("")}</ul>`;
}

export function renderStreetMeta(params: {
  updatedAt: string | null;
  agents: number;
  awake: number;
  bankroll: string;
}): string {
  if (!params.updatedAt) {
    return "The street has not run yet. Start the runtime and this page comes alive.";
  }
  return `${params.awake} of ${params.agents} agents awake · ${formatEth(
    params.bankroll
  )} on the street · updated ${timeOf(params.updatedAt)} UTC`;
}
